import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { base44 } from "@/api/base44Client";
import { useAuth } from "@/lib/AuthContext";
import AppHeader from "@/components/drill/AppHeader";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { FileText, Plus, ChevronRight, X, Save, Trash2 } from "lucide-react";
import { isAtLeast } from "@/lib/roles";
import { getTotalDepth } from "@/lib/depthCalculations";

import { safeLocalStorage } from '@/lib/browserStorage';
const STORAGE_KEY = "drilllog_handover_forms";

function loadForms() {
  try { return JSON.parse(safeLocalStorage.getItem(STORAGE_KEY) || "[]"); } catch { return []; }
}
function saveForms(forms) { safeLocalStorage.setItem(STORAGE_KEY, JSON.stringify(forms)); }

function HoleSummary({ hole }) {
  if (!hole) return <div className="text-sm text-slate-500">No active hole found.</div>;
  const items = [
    ["Rig", hole.rig_id], ["Method", hole.hole_type], ["Status", hole.status || "Active"],
    ["Current Depth", hole.depth != null ? `${Number(hole.depth).toFixed(2)} m` : null],
    ["Barrel Length", hole.barrel_length != null ? `${hole.barrel_length} m` : null],
    ["Table Height", hole.table_height != null ? `${hole.table_height} m` : null],
    ["Barrel-Table Height", hole.barrel_table_height != null ? `${hole.barrel_table_height} m` : null],
    ["Rod Length", hole.run_size != null ? `${hole.run_size} m` : null],
    ["BHA Length", hole.bha_length != null ? `${hole.bha_length} m` : null],
    ["Casing Depth", hole.casing_installed_depth != null ? `${hole.casing_installed_depth} m` : null],
    ["Rods in Hole", hole.rods_in_hole], ["Total Rod Count", hole.total_rod_count],
  ];
  return (
    <div className="rounded-xl border bg-slate-50 p-3 dark:bg-slate-950/40">
      <div className="text-[11px] uppercase tracking-wide text-slate-500">Current Hole</div><div className="font-semibold text-base">{hole.name || "Unknown hole"}</div>
      <div className="mt-2 grid grid-cols-2 gap-x-4 gap-y-1 text-xs text-slate-600 dark:text-slate-300">
        {items.filter(([, value]) => value != null && value !== "").map(([label, value]) => <div key={label}>{label}: <b>{value}</b></div>)}
      </div>
      {hole.description && <div className="mt-2 text-xs text-slate-500 dark:text-slate-400">{hole.description}</div>}
    </div>
  );
}

export default function Handover({ formId = null }) {
  const navigate = useNavigate();
  const { effectiveRole, user } = useAuth();
  const [holes, setHoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [forms, setForms] = useState(loadForms);
  const [shoppingList, setShoppingList] = useState(false);

  const isForm = Boolean(formId);
  const selectedForm = useMemo(() => forms.find((f) => f.id === formId), [forms, formId]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [sheets, runs] = await Promise.all([
          base44.entities.Sheet.list("-created_date"),
          base44.entities.DrillRun.list(),
        ]);
        let active = (sheets || []).filter((s) => String(s.status || "Active") !== "Completed");
        if (effectiveRole === "rig") {
          const ids = new Set([user?.rig_id, user?.full_name, user?.name, user?.id].filter(Boolean).map(String));
          active = active.filter((s) => ids.has(String(s.rig_id || "")) || ids.has(String(s.rig_user_id || "")));
        }
        const withDepth = active.map((s) => {
          const sheetRuns = (runs || []).filter((r) => r.sheet_id === s.id);
          const maxDepth = sheetRuns
            .map((r) => getTotalDepth(r, s.hole_type, s.run_size))
            .filter((d) => Number.isFinite(d))
            .reduce((m, d) => Math.max(m, d), 0);
          const withRodCount = sheetRuns.filter((r) => r.total_rod_count != null && r.total_rod_count !== "");
          const latestRod = [...withRodCount].sort((a,b) => {
            const ad = getTotalDepth(a, s.hole_type, s.run_size) ?? (Number(a.from_depth) || 0), bd = getTotalDepth(b, s.hole_type, s.run_size) ?? (Number(b.from_depth) || 0);
            return bd - ad || String(b.updated_date || b.created_date || "").localeCompare(String(a.updated_date || a.created_date || ""));
          })[0];
          const deepest = [...sheetRuns].sort((a,b) => (getTotalDepth(b, s.hole_type, s.run_size) ?? Number(b.from_depth) ?? 0)-(getTotalDepth(a, s.hole_type, s.run_size) ?? Number(a.from_depth) ?? 0))[0];
          return {
            ...s,
            depth: maxDepth,
            rods_in_hole: deepest?.rods_in_hole,
            total_rod_count: latestRod?.total_rod_count,
            latest_run: deepest || null,
          };
        });
        if (!cancelled) setHoles(withDepth);
      } catch (e) { console.error(e); }
      if (!cancelled) setLoading(false);
    })();
    if (selectedForm) setShoppingList(Boolean(selectedForm.shopping_list));
    else setShoppingList(false);
    return () => { cancelled = true; };
  }, [effectiveRole, user, selectedForm]);

  const currentHole = useMemo(() => {
    if (!holes.length) return null;
    return [...holes].sort((a, b) =>
      new Date(b.updated_date || b.created_date || 0) - new Date(a.updated_date || a.created_date || 0)
    )[0];
  }, [holes]);

  const handleDelete = (id) => {
    if (!window.confirm("Delete this saved handover? This cannot be undone.")) return;
    const next = forms.filter((f) => f.id !== id);
    setForms(next);
    saveForms(next);
  };

  const handleSave = () => {
    const id = selectedForm?.id || `handover-${Date.now()}`;
    const existing = selectedForm || {};
    const record = {
      ...existing,
      id,
      created_at: existing.created_at || new Date().toISOString(),
      updated_at: new Date().toISOString(),
      shopping_list: shoppingList,
      hole: currentHole ? {
        id: currentHole.id,
        name: currentHole.name,
        rig_id: currentHole.rig_id,
        hole_type: currentHole.hole_type,
        status: currentHole.status,
        description: currentHole.description,
        depth: currentHole.depth,
        barrel_length: currentHole.barrel_length,
        table_height: currentHole.table_height,
        barrel_table_height: currentHole.barrel_table_height,
        run_size: currentHole.run_size,
        bha_length: currentHole.bha_length,
        casing_installed_depth: currentHole.casing_installed_depth,
        collars: currentHole.collars || [],
        rods_in_hole: currentHole.rods_in_hole,
        total_rod_count: currentHole.total_rod_count,
      } : null,
    };
    const next = selectedForm ? forms.map((f) => f.id === id ? record : f) : [record, ...forms];
    setForms(next);
    saveForms(next);
    return id;
  };

  if (isForm) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-slate-50 to-slate-100 dark:from-slate-950 dark:to-slate-900">
        <AppHeader title="Handover" subtitle="Create handover record" backTo="/handover" />
        <main className="max-w-2xl mx-auto px-4 sm:px-6 py-6 pb-32">
          <div className="space-y-4">
            <section className="rounded-2xl border bg-white p-4 dark:bg-slate-900 dark:border-slate-800">
              <h2 className="font-semibold mb-3">Current Hole</h2>
              {loading ? <div className="text-sm text-slate-500">Loading active hole…</div> : <HoleSummary hole={currentHole} />}
            </section>
            <section className="rounded-2xl border bg-white p-4 dark:bg-slate-900 dark:border-slate-800">
              <div className="flex items-center justify-between gap-4">
                <div>
                  <h2 className="font-semibold">Shopping List</h2>
                  <p className="text-xs text-slate-500 mt-1">Is a shopping list required?</p>
                </div>
                <div className="flex items-center gap-2 text-xs font-medium">
                  <span className={!shoppingList ? "text-slate-900 dark:text-white" : "text-slate-400"}>No</span>
                  <Switch checked={shoppingList} onCheckedChange={setShoppingList} aria-label="Shopping List" />
                  <span className={shoppingList ? "text-slate-900 dark:text-white" : "text-slate-400"}>Yes</span>
                </div>
              </div>
            </section>
          </div>
          <div className="fixed bottom-0 left-0 right-0 z-30 border-t bg-white/95 p-3 backdrop-blur dark:bg-slate-950/95 dark:border-slate-800">
            <div className="max-w-2xl mx-auto grid grid-cols-3 gap-2">
              <Button variant="outline" className="rounded-xl" onClick={() => navigate("/handover")}><X className="w-4 h-4 mr-1" />Close</Button>
              <Button variant="outline" className="rounded-xl" onClick={() => { handleSave(); }}><Save className="w-4 h-4 mr-1" />Save</Button>
              <Button className="rounded-xl" onClick={() => { handleSave(); navigate("/handover"); }}><Save className="w-4 h-4 mr-1" />Save and exit</Button>
            </div>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-slate-100 dark:from-slate-950 dark:to-slate-900">
      <AppHeader title="Handover" subtitle="Field handover records" backTo="/" />
      <main className="max-w-2xl mx-auto px-4 sm:px-6 py-6 pb-24">
        <Button className="w-full h-12 rounded-xl" onClick={() => navigate("/handover/new")}><Plus className="w-5 h-5 mr-2" />New</Button>
        <div className="mt-4 space-y-2">
          {forms.length === 0 ? (
            <div className="rounded-2xl border bg-white p-6 text-center text-sm text-slate-500 dark:bg-slate-900 dark:border-slate-800">No handover forms saved.</div>
          ) : forms.map((form) => (
            <div key={form.id} className="w-full rounded-xl border bg-white p-3 shadow-sm dark:bg-slate-900 dark:border-slate-800">
              <div className="flex items-center gap-3">
                <button onClick={() => navigate(`/handover/${form.id}`)} className="min-w-0 flex-1 text-left hover:bg-slate-50 dark:hover:bg-slate-800 rounded-lg -m-1 p-1">
                  <div className="flex items-center gap-3">
                    <FileText className="w-5 h-5 shrink-0" />
                    <div className="min-w-0 flex-1">
                      <div className="font-semibold truncate">Handover — {form.hole?.name || "No active hole"}</div>
                      <div className="text-xs text-slate-500 mt-1">Shopping List: {form.shopping_list ? "Yes" : "No"}</div>
                    </div>
                    <ChevronRight className="w-4 h-4 shrink-0 text-slate-400" />
                  </div>
                </button>
                <Button variant="ghost" size="icon" onClick={() => handleDelete(form.id)} className="shrink-0 h-10 w-10 text-red-500 hover:text-red-600" title="Delete handover" aria-label="Delete handover">
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      </main>
    </div>
  );
}
